import axios from 'axios'

import { store } from '../app/store'
import { unsetAuth, setAuth } from '../app/auth/actions'
import oktaAuth from '../app/authConfig'

const instance = axios.create({
  baseURL: `${OE_ENV.API_GATEWAY_URL}/auth`,
  withCredentials: true,
})

const oktaInstanceApiV1 = axios.create({
  baseURL: `${OE_ENV.OKTA_BASE_URL}/api/v1`,
  withCredentials: true,
})

const extractDataFromError = (error) => {
  console.log(`Error in authentication API: ${JSON.stringify(error, null, 2)}`)
  const {
    response: { data },
  } = error
  return data
}

const extractErrors = (error) => {
  const {
    errorSummary,
    errorCauses,
    errorCode = '',
    errorData = {},
  } = extractDataFromError(error)
  const errorItems = errorCauses.map((errorCause) => errorCause?.errorSummary)
  return { errorCode, errorData, errorSummary, errorItems }
}

const extractValidatorAPiErrors = (error) => {
  const {
    response: {
      data: { errors },
    },
  } = error
  const errorItems = errors.map(({ message }) => message)
  return { errorSummary: 'There were validation errors', errorItems }
}

const baseStoreOperation =
  (store) =>
  (prefix) =>
  (operation) =>
  (key, ...args) =>
    store[`${operation}Item`]([prefix, key].join('.'), ...args)

const localStoragePrefixedOperation = baseStoreOperation(localStorage)(
  'AuthIdentityProvider.OKTA',
)

const storeSetItem = localStoragePrefixedOperation('set')
const storeGetItem = localStoragePrefixedOperation('get')
const storeRemoveItem = localStoragePrefixedOperation('remove')

const setIdentityIntoLocalStorage = (idToken) => {
  let profile, username
  try {
    const payload = idToken?.split('.')[1]
    if (!payload) {
      throw new Error(`Invalid token`, idToken)
    }
    const claims = JSON.parse(atob(payload))
    username = claims['login']
    if (!username) {
      throw new Error(`Invalid token`, idToken)
    }
    profile = {
      brokerId: claims['brokerId'],
      email: claims['email'],
      expiration: claims['expiresAt'],
      firstName: claims['firstName'],
      id: claims['id'],
      lastName: claims['lastName'],
      permissions: claims['scopes']?.split(','),
      phone: claims['phone'],
      phoneExt: claims['phoneExt'],
      role: claims['role'],
      status: claims['status'],
      title: claims['title'],
      username,
    }
  } catch (error) {
    console.log(`Error decoding jwt`, error)
  }

  storeSetItem('lastUser', username)
  storeSetItem(`${username}.idToken`, idToken)
  storeSetItem(`${username}.userData`, JSON.stringify(profile))
}

export const getIdentityFromLocalStorage = () => {
  const username = storeGetItem('lastUser')
  return storeGetItem(`${username}.idToken`)
}

export const getUserDataFromLocalStorage = () => {
  const username = storeGetItem('lastUser')
  return JSON.parse(storeGetItem(`${username}.userData`))
}

export const removeIdentityFromLocalStorage = () => {
  const username = storeGetItem('lastUser')
  storeRemoveItem(`${username}.idToken`)
  storeRemoveItem(`${username}.userData`)
}

// Signs into OKTA via auth proxy - create profile for localStorage on success
// session is "hidden" in a secure, httpOnly cookie for the api.[env].ownersedge.us domain
// so don't worry about it :)
export const authenticate = async (username, password) => {
  try {
    // const { data: { idToken, status } } = await instance.post('/sign-in', { username, password })
    await instance.post('/sign-in', { username, password })

    oktaAuth.redirect({ redirectUri: '/complete-profile' })

    return { success: true, error: null }
  } catch (error) {
    return { success: false, error: extractErrors(error) }
  }
}

export const unlock = async (username) => {
  try {
    const { accessToken } = await getCurrentSession()
    await instance.post(
      '/unlock-user',
      { username },
      {
        headers: {
          Authorization: accessToken,
        },
      },
    )
    return { success: true, error: null }
  } catch (error) {
    return { success: false, error: extractErrors(error) }
  }
}

export const getLockedUsers = async (username) => {
  try {
    const { accessToken } = await getCurrentSession()
    const data = await instance.get('/get-locked-users', {
      headers: {
        Authorization: accessToken,
      },
    })
    return { success: true, error: null, data }
  } catch (error) {
    return { success: false, error: extractErrors(error) }
  }
}

export const signOut = async () => {
  try {
    removeIdentityFromLocalStorage()
    oktaAuth.tokenManager.clear()
    await instance.get('/sign-out')
    store.dispatch(unsetAuth())
    return true
  } catch (error) {
    throw error
  }
}

export const signOutIdx = async () => {
  try {
    removeIdentityFromLocalStorage()
    await oktaInstanceApiV1.delete('/sessions/me')
    // For new Okta Identity Engine, sign-out api call will return delete cookie only response
    await instance.get('/sign-out')
    oktaAuth._oktaAuth.tokenManager.clear()
    store.dispatch(unsetAuth())
    return true
  } catch (error) {
    throw error
  }
}

export const signUp = async (username) => {
  try {
    const {
      data: { status, reason },
    } = await instance.post('/sign-up', { username })
    return { status, reason }
  } catch (error) {
    // console.error(error)
  }
  return { status: 'REQUEST_ACCESS', reason: 'REJECTED' }
}

export const verifyRecoveryToken = async (recoveryToken) => {
  try {
    const {
      data: { username, policy },
    } = await instance.post('/verify-recovery-token', { recoveryToken })

    return { username, policy }
  } catch (error) {
    return false
  }
}

export const resetPasswordLegacy = async (
  recoveryToken,
  newPassword,
  onSuccess,
  onFailure,
) => {
  try {
    const {
      data: { idToken, status },
    } = await instance.post('/reset-password', { recoveryToken, newPassword })

    setIdentityIntoLocalStorage(idToken)

    return onSuccess({ idToken, status })
  } catch (error) {
    return onFailure(error.response)
  }
}

export const webfinger = async (username) => {
  try {
    const { data: finger } = await instance.post('/webfinger', { username })
    return finger
  } catch (error) {
    console.log(error)
    return null
  }
}

export const invite = async (username, origin, role, brokerId, forceUpdate = false) => {
  try {
    const { accessToken } = await getCurrentSession()
    await instance.post(
      '/invite',
      { username, origin, role, brokerId, forceUpdate },
      {
        headers: {
          Authorization: accessToken,
        },
      },
    )

    return { success: true, error: null }
  } catch (error) {
    // throw for supporting user/sagas
    throw extractErrors(error)
  }
}

export const verifyInviteToken = async (inviteToken) => {
  try {
    const {
      data: { username, policy },
    } = await instance.post('/verify-invite-token', { inviteToken })

    return { username, policy }
  } catch (error) {
    return false
  }
}

export const completeInvite = async (inviteToken, newPassword) => {
  try {
    await instance.post('/complete-invite', { inviteToken, newPassword })
    oktaAuth.signInWithRedirect({
      originalUri: '/complete-profile', // The URL to redirect to after login
    })

    return { success: true, error: null }
  } catch (error) {
    console.log('Error in Complete Invite', JSON.stringify(error, null, 2))
    return { success: false, error: extractErrors(error) }
  }
}

export const deleteInvite = async (username) => {
  try {
    await instance.delete('/invite', { username })
    return true
  } catch (error) {
    console.log(error)
    return false
  }
}

export const updateProfile = async (firstName, lastName, phone, phoneExt, title) => {
  try {
    const { accessToken } = await getCurrentSession()
    const {
      data: { idToken },
    } = await instance.post(
      '/update-profile-idx',
      {
        firstName,
        lastName,
        phone,
        phoneExt,
        title,
      },
      {
        headers: {
          Authorization: accessToken,
        },
      },
    )

    setIdentityIntoLocalStorage(idToken)

    return { success: true, error: null }
  } catch (error) {
    let handledError
    try {
      handledError = extractValidatorAPiErrors(error)
    } catch (err) {
      handledError = extractErrors(error)
    }
    return { success: false, error: handledError }
  }
}

export const validateSession = async () => {
  let accessToken
  try {
    /**
     * getCurrentSession won't always have the accessToken when this function runs
     * so use oktaAuth to get the accessToken.
     */
    accessToken = await oktaAuth.getAccessToken()
    if (accessToken === undefined || accessToken === null) {
      throw Error('accessToken not retrieved...')
    }
  } catch (err) {
    console.log(err.message)
  }
  if (accessToken !== undefined && accessToken !== null) {
    try {
      const response = await instance.post(
        '/getOwnersEdgeIdToken',
        {},
        {
          headers: {
            Authorization: accessToken,
          },
        },
      )

      if (response && response.data) {
        const idxToken = response.data.idToken
        const idxStatus = response.data.status
        if (!idxToken || idxStatus !== 'active') {
          throw new Error('Error getting idToken or status')
        } else {
          setIdentityIntoLocalStorage(idxToken)
          store.dispatch(setAuth())
          return { idToken: idxToken, status: idxStatus }
        }
      }
    } catch (error) {
      console.log(error.message)
      await oktaAuth.signOut()
    }
  }

  removeIdentityFromLocalStorage()

  return { idToken: null, status: null }
}

export const getCurrentSession = async () => {
  const userData = getUserDataFromLocalStorage()
  const tokenManager = oktaAuth.tokenManager
  const accessToken = await tokenManager.get('accessToken')
  return { userData, accessToken: accessToken.accessToken }
}

// Wrappers around old, stupid code for obtaining default preferences and applying
// more broker-related data to the user object, because why not.

export const getUserDefaultPreferences = async (id) => {
  const api = apigClientFactory.newClient({})

  try {
    const { accessToken } = await getCurrentSession()
    const {
      data: { product_defaults: defaultPreference },
    } = await api.usersIdPreferencesGet(
      { id: id },
      {},
      {
        headers: {
          Authorization: accessToken,
        },
      },
    )

    return { defaultPreference }
  } catch (error) {
    return { defaultPreference: {} }
  }
}

export const getUserBrokerData = async (brokerId) => {
  const api = apigClientFactory.newClient({})
  const { accessToken } = await getCurrentSession()

  if (brokerId) {
    const {
      data: { data: brokerage },
    } = await api.apiGetBrokerIdGet(
      { id: brokerId },
      {},
      {
        headers: {
          Authorization: accessToken,
        },
      },
    )

    const brokerName = brokerage?.name ?? 'Argo Underwriters'
    const bundles = brokerage?.bundles ?? []

    return { brokerage, brokerName, bundles }
  }

  return { brokerage: {}, brokerName: 'Argo Underwriters', bundles: [] }
}

export const getDeductiblesData = async (stateCode) => {
  const api = apigClientFactory.newClient({})
  const { accessToken } = await getCurrentSession()

  if (stateCode) {
    const { data: deductibles } = await api.apiGetDeductiblesIdGet(
      { id: stateCode },
      {},
      {
        headers: {
          Authorization: accessToken,
        },
      },
    )

    return deductibles
  }

  return {}
}
